<div class="ocr-pane">
  <div class="ocr-pane-head">
    <div>
      <p class="step">Step 2 — Review queue</p>
      <h2>Documents</h2>
      @if (counts().pending > 0) {
        <p class="hint polling">
          <svg class="hint-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3.5-7.14" /></svg>
          Extraction in progress…
        </p>
      }
    </div>
    <button type="button" class="btn" (click)="refresh()" [disabled]="loading()">
      {{ loading() ? 'Refreshing…' : 'Refresh' }}
    </button>
  </div>

  @if (error()) {
    <p class="ocr-msg error">{{ error() }}</p>
  }

  <div class="stat-grid">
    <button
      type="button"
      class="stat-tile t-review"
      [class.on]="stateFilter() === 'needs_review'"
      [class.zero]="counts().needs_review === 0"
      (click)="setState('needs_review')"
    >
      <span class="stat-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 8v4l2.5 2.5" /><circle cx="12" cy="12" r="9" /></svg>
      </span>
      <span class="stat-body">
        <span class="stat-value">{{ counts().needs_review }}</span>
        <span class="stat-label">Needs review</span>
      </span>
    </button>
    <button
      type="button"
      class="stat-tile t-pending"
      [class.on]="stateFilter() === 'pending'"
      [class.zero]="counts().pending === 0"
      (click)="setState('pending')"
    >
      <span class="stat-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3.5-7.14" /><path d="M21 3v6h-6" /></svg>
      </span>
      <span class="stat-body">
        <span class="stat-value">{{ counts().pending }}</span>
        <span class="stat-label">Processing</span>
      </span>
    </button>
    <button
      type="button"
      class="stat-tile t-resolved"
      [class.on]="stateFilter() === 'resolved'"
      [class.zero]="counts().resolved === 0"
      (click)="setState('resolved')"
    >
      <span class="stat-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 12 2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg>
      </span>
      <span class="stat-body">
        <span class="stat-value">{{ counts().resolved }}</span>
        <span class="stat-label">Case created</span>
      </span>
    </button>
    <button
      type="button"
      class="stat-tile t-rejected"
      [class.on]="stateFilter() === 'rejected'"
      [class.zero]="counts().rejected === 0"
      (click)="setState('rejected')"
    >
      <span class="stat-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 9-6 6M9 9l6 6" /><circle cx="12" cy="12" r="9" /></svg>
      </span>
      <span class="stat-body">
        <span class="stat-value">{{ counts().rejected }}</span>
        <span class="stat-label">Rejected</span>
      </span>
    </button>
    <button
      type="button"
      class="stat-tile t-failed"
      [class.on]="stateFilter() === 'failed'"
      [class.zero]="counts().failed === 0"
      (click)="setState('failed')"
    >
      <span class="stat-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 9v4M12 17h.01" /><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L14.71 3.86a2 2 0 0 0-3.42 0Z" /></svg>
      </span>
      <span class="stat-body">
        <span class="stat-value">{{ counts().failed }}</span>
        <span class="stat-label">Failed</span>
      </span>
    </button>
  </div>

  <div class="document-toolbar">
    <span>{{ filtered().length }} item{{ filtered().length === 1 ? '' : 's' }}</span>
    <label class="doc-search">
      <span class="visually-hidden">Search</span>
      <input
        type="search"
        [ngModel]="searchQuery()"
        (ngModelChange)="searchQuery.set($event)"
        placeholder="Search by applicant, subject, batch…"
        aria-label="Search documents"
      />
    </label>
  </div>

  @if (loading() && !filtered().length) {
    <div class="empty-box">Loading…</div>
  } @else if (!filtered().length) {
    <div class="empty-box">No documents match. Capture one to see it appear here.</div>
  } @else {
    <div class="table-wrap" tabindex="0" aria-label="Documents table; scroll horizontally to see all columns">
      <table>
        <thead>
          <tr>
            <th>Case</th>
            <th>Batch</th>
            <th>Match</th>
            <th>Status</th>
            <th>Updated</th>
            <th class="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (r of filtered(); track r.key) {
            <tr>
              <td>
                <strong class="case-name" lang="hi" [attr.title]="r.title">{{ r.title }}</strong>
                @if (r.subtitle) {
                  <div class="sub" [attr.title]="r.subtitle">{{ r.subtitle }}</div>
                }
              </td>
              <td>#{{ r.batch.id }}</td>
              <td>
                @if (r.extraction?.matchConfidence != null) {
                  <span class="match-tier" [class]="matchTier(r.extraction!.matchConfidence!)">
                    {{ matchTierLabel[matchTier(r.extraction!.matchConfidence!)] }}
                  </span>
                } @else {
                  —
                }
              </td>
              <td>
                <span [class]="'pill s-' + r.state">
                  @if (r.state === 'pending') {
                    <svg class="pill-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3.5-7.14" /></svg>
                  }
                  {{ stateLabel[r.state] }}
                </span>
              </td>
              <td>{{ r.updatedAt | date: 'd MMM y, HH:mm' }}</td>
              <td class="col-actions">
                <div class="row-actions">
                  @if (r.state === 'needs_review' && r.extraction) {
                    <a class="row-btn" [routerLink]="['/document-ocr', 'review', r.extraction.id]">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                      Review
                    </a>
                  }
                  @if (r.state === 'resolved' && r.extraction?.grievanceRef) {
                    <a class="row-btn" [routerLink]="['/complaints', r.extraction!.grievanceRef]">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" /><circle cx="12" cy="12" r="3" /></svg>
                      View case
                    </a>
                  }
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }
</div>
